/* Reusable admin media picker.
 *
 * Renders a preview of the current image or video with three actions:
 * Upload/Replace, Paste URL (fallback for external hosts like
 * Unsplash), and Remove. Uploads go through /api/admin/upload which
 * handles both images (≤10MB) and short-form videos (≤100MB).
 *
 * The parent gets a plain URL string via `onChange(url)` — no
 * behaviour changes vs. the old text-input, so every existing saved
 * URL keeps working.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Trash2, Link as LinkIcon, Image as ImageIcon,
         Video, RefreshCcw, AlertTriangle } from 'lucide-react';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VIDEO_RX = /\.(mp4|webm|mov)(\?|$)/i;

export const isVideoUrl = (u) => !!u && VIDEO_RX.test(u);

const MediaField = ({
  value,
  onChange,
  label,
  accept = 'image/*',           // 'image/*' | 'video/*' | 'image/*,video/*'
  hint,
  testid = 'media-field',
  aspectClass = 'aspect-video', // preview aspect ratio (Tailwind)
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlDraft, setUrlDraft] = useState(value || '');

  const acceptsVideo = accept.includes('video');
  const acceptsImage = accept.includes('image');
  const isVideo = isVideoUrl(value);

  const upload = async (file) => {
    if (!file) return;
    setError(''); setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      if (!res.data?.url) throw new Error('Server returned no url.');
      // Cache-bust so browsers immediately show the replacement.
      const bust = res.data.url + (res.data.url.includes('?') ? '&' : '?')
                    + 'v=' + Date.now();
      onChange(bust);
      setUrlDraft(bust);
    } catch (e) {
      logger.error('media upload failed', e);
      setError(e.response?.data?.detail || e.message || 'Upload failed.');
    } finally {
      setUploading(false); setProgress(0);
    }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
    e.target.value = '';   // let the same file be re-picked later
  };

  const commitUrl = () => {
    const v = (urlDraft || '').trim();
    if (v && !/^https?:\/\//i.test(v)) {
      setError('URL must start with http:// or https://');
      return;
    }
    setError('');
    onChange(v);
    setShowUrlInput(false);
  };

  const remove = () => {
    onChange('');
    setUrlDraft('');
    setError('');
    setShowUrlInput(false);
  };

  return (
    <div data-testid={testid} className="w-full">
      {label && (
        <label className="block text-xs font-medium text-[#4B5563] mb-1">
          {label}
        </label>
      )}

      {/* Preview */}
      <div className={`relative w-full ${aspectClass} bg-[#F8F9FA] border border-[#E5E7EB]
                        rounded-md overflow-hidden flex items-center justify-center`}
           data-testid={`${testid}-preview`}>
        {value ? (
          isVideo ? (
            <video src={value} controls className="w-full h-full object-contain bg-black"
                   data-testid={`${testid}-video-preview`}>
              Your browser cannot play this video.
            </video>
          ) : (
            <img src={value} alt={label || 'preview'}
                 className="w-full h-full object-cover"
                 onError={(e) => { e.currentTarget.style.opacity = '0.3'; setError('Image failed to load — the URL may be broken. Try re-uploading.'); }}
                 data-testid={`${testid}-image-preview`} />
          )
        ) : (
          <div className="text-center text-[#9CA3AF]">
            {acceptsVideo && !acceptsImage
              ? <Video className="w-8 h-8 mx-auto mb-1" />
              : <ImageIcon className="w-8 h-8 mx-auto mb-1" />}
            <p className="text-xs">No media selected</p>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-white">
            <RefreshCcw className="w-6 h-6 animate-spin mb-1" />
            <p className="text-xs">Uploading… {progress}%</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-2 flex-wrap" data-testid={`${testid}-actions`}>
        <button type="button" onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="px-3 py-1.5 text-xs bg-[#0A0A0A] text-white rounded hover:bg-black flex items-center gap-1 disabled:opacity-50"
          data-testid={`${testid}-upload-btn`}>
          <Upload className="w-3.5 h-3.5" /> {value ? 'Replace' : 'Upload'}
        </button>
        <button type="button" onClick={() => setShowUrlInput((v) => !v)}
          className="px-3 py-1.5 text-xs bg-white text-[#0A0A0A] border border-[#E5E7EB] rounded hover:bg-[#F8F9FA] flex items-center gap-1"
          data-testid={`${testid}-url-btn`}>
          <LinkIcon className="w-3.5 h-3.5" /> Paste URL
        </button>
        {value && (
          <button type="button" onClick={remove}
            className="px-3 py-1.5 text-xs bg-white text-[#B91C1C] border border-[#FCA5A5] rounded hover:bg-[#FEF2F2] flex items-center gap-1"
            data-testid={`${testid}-remove-btn`}>
            <Trash2 className="w-3.5 h-3.5" /> Remove
          </button>
        )}
        <input type="file" accept={accept} ref={inputRef} onChange={onPick}
               className="hidden" data-testid={`${testid}-file-input`} />
      </div>

      {/* URL input drawer */}
      {showUrlInput && (
        <div className="mt-2 flex gap-2" data-testid={`${testid}-url-drawer`}>
          <input type="text" value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            placeholder="https://..."
            className="flex-1 px-2 py-1.5 border border-[#E5E7EB] rounded-sm text-sm"
            data-testid={`${testid}-url-input`} />
          <button type="button" onClick={commitUrl}
            className="px-3 py-1.5 text-xs bg-[#BA1826] text-white rounded"
            data-testid={`${testid}-url-save`}>Set URL</button>
        </div>
      )}

      {hint && !error && (
        <p className="mt-1.5 text-[10px] text-[#9CA3AF] leading-relaxed">{hint}</p>
      )}
      {error && (
        <p className="mt-1.5 text-xs text-[#B91C1C] flex items-start gap-1"
           data-testid={`${testid}-error`}>
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
};

export default MediaField;
